import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Copy, Eye, EyeOff, RefreshCw, AlertTriangle, Shield, BookOpen, Sparkles, Terminal, Lock, Download, CheckCircle2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Switch } from '../../../../components/ui/switch';
import { writeClipboardText } from '../../../../lib/clipboard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import type {
  McpServerConfig,
  McpPermissionLevel,
  McpServerStatus,
  McpPortMode,
  ConnectionsAddPermission,
  MetaInfoWritePermission,
} from '../../../../types/mcpServerTypes';

type SettingsRowProps = {
  title: string;
  description: string;
  htmlFor: string;
  children: ReactNode;
  testId?: string;
  warning?: string;
};

function SettingsRow({ title, description, htmlFor, children, testId, warning }: SettingsRowProps) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-background p-4" data-testid={testId}>
      <div className="grid gap-0.5 flex-1">
        <Label htmlFor={htmlFor} className="cursor-pointer">
          {title}
        </Label>
        <div className="text-xs text-muted-foreground">{description}</div>
        {warning && (
          <div className="flex items-center gap-1.5 mt-1.5 text-xs text-yellow-600 dark:text-yellow-500">
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            <span>{warning}</span>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    void writeClipboardText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [value]);

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCopy}
      disabled={!value}
      title={`Copy ${label}`}
    >
      <Copy className="h-3.5 w-3.5 mr-1.5" />
      {copied ? 'Copied!' : 'Copy'}
    </Button>
  );
}

const permissionLabels: Record<McpPermissionLevel, string> = {
  'block': 'Blocked',
  'read-only': 'Read-only (SELECT only)',
  'confirm': 'Read + Write with approval',
};

const permissionDescriptions: Record<McpPermissionLevel, string> = {
  'block': 'AI cannot execute any queries',
  'read-only': 'AI can only run SELECT queries (recommended)',
  // Enforced by `guardUiSqlExecution` (electron/mcp-ui-control/uiExecutionGuard.ts) →
  // `requestWriteApproval`. The Oracle sentence is the honest carve-out: the PL/SQL debug tools
  // execute code rather than a query, so they answer to the level instead of the classifier.
  // `debug_evaluate_expression` raises no dialog because it grants nothing to prompt about: on the
  // agent path it returns only what the debugger reads out of the frame and refuses anything else
  // (`AGENT_EVALUATE_REFUSAL` in electron/oracle-debugger/expressionEvaluator.ts).
  'confirm': 'AI needs your approval before running any write query. On Oracle this also unlocks the PL/SQL debug tools; an AI can read variables in a paused debug session but cannot evaluate expressions that would run code.',
};

function BrowserModeUpsell() {
  const features = [
    {
      icon: Terminal,
      title: 'MCP Server',
      description: 'Connect Claude, Codex, and other AI tools directly to your databases via Model Context Protocol.',
    },
    {
      icon: Sparkles,
      title: 'AI Workspace',
      description: 'Integrated terminal with file sync lets AI agents edit your SQL queries in real-time.',
    },
    {
      icon: Lock,
      title: 'Privacy Controls',
      description: 'Fine-grained permissions, audit logging, and read-only defaults keep your data secure.',
    },
  ];

  return (
    <div className="space-y-6 max-w-xl">
      <div className="text-center space-y-3 py-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-2">
          <Sparkles className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-lg font-semibold">AI Integrations</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Supercharge your workflow by connecting AI coding assistants to Jam SQL Studio.
          Available in the desktop app.
        </p>
      </div>

      <div className="space-y-3">
        {features.map((feature) => (
          <div
            key={feature.title}
            className="flex gap-4 p-4 rounded-lg border border-border bg-card"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <feature.icon className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">{feature.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {feature.description}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-3 pt-2">
        <a
          href="https://jamsql.com/?utm_source=app&utm_medium=referral&utm_campaign=ai-integrations-settings#download"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
        >
          <Download className="h-4 w-4" />
          Download Desktop App
        </a>
        <a
          href="https://jamsql.com/docs/ai-integrations-mcp?utm_source=app&utm_medium=referral&utm_campaign=ai-integrations-settings"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
        >
          <BookOpen className="h-4 w-4" />
          Learn more about AI Integrations
        </a>
      </div>
    </div>
  );
}

const SKILL_COMMAND = 'jam-sql add-skill';

function SkillSnippet() {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    writeClipboardText(SKILL_COMMAND).then((ok) => {
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    });
  }, []);

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2">
      <p className="text-sm text-muted-foreground">
        Add the Jam SQL skill to your favorite AI agent:
      </p>
      <div className="flex items-center gap-2">
        <code className="flex-1 rounded-md bg-background px-3 py-2 text-sm font-mono select-all">
          {SKILL_COMMAND}
        </code>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={handleCopy}
          data-testid="copy-skill-command-btn"
        >
          {copied ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

export function AiIntegrationsSettingsTab() {
  const [config, setConfig] = useState<McpServerConfig | null>(null);
  const [status, setStatus] = useState<McpServerStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showToken, setShowToken] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isBrowserMode] = useState(() => !window.electronAPI?.mcpServer);
  const [claudeCodeCopied, setClaudeCodeCopied] = useState(false);

  // Local state for number inputs
  const [maxRowsInput, setMaxRowsInput] = useState('');
  const [timeoutInput, setTimeoutInput] = useState('');
  const [preferredPortInput, setPreferredPortInput] = useState('');

  // Load initial config
  const loadConfig = useCallback(async () => {
    const api = window.electronAPI?.mcpServer;
    if (!api) {
      setLoading(false);
      return;
    }

    try {
      const result = await api.getConfig();
      if (result.success && result.config) {
        setConfig(result.config);
        setMaxRowsInput(String(result.config.maxRows));
        setTimeoutInput(String(result.config.queryTimeoutSeconds));
        setPreferredPortInput(String(result.config.preferredPort || 12345));
        if (result.status) {
          setStatus(result.status);
        }
      } else {
        setError(result.error ?? 'Failed to load configuration');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Update config helper. Returns whether the update actually succeeded so
  // callers can fire analytics on success, not on click.
  const updateConfig = useCallback(async (updates: Partial<McpServerConfig>): Promise<boolean> => {
    const api = window.electronAPI?.mcpServer;
    if (!api || !config) return false;

    try {
      const result = await api.updateConfig(updates);
      if (result.success && result.config) {
        setConfig(result.config);
        if (result.status) {
          setStatus(result.status);
        }
        return true;
      } else {
        setError(result.error ?? 'Failed to update configuration');
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    }
  }, [config]);

  // Toggle server enabled
  const handleToggleEnabled = useCallback(async (enabled: boolean) => {
    const ok = await updateConfig({ enabled });
    if (ok) {
      window.electronAPI?.analytics?.track('ui:mcp_server_enabled_toggled', { enabled }).catch(() => {});
    }
  }, [updateConfig]);

  // Rotate token
  const handleRotateToken = useCallback(async () => {
    const api = window.electronAPI?.mcpServer;
    if (!api || !config) return;

    setRotating(true);
    try {
      const result = await api.rotateToken();
      if (result.success && result.token) {
        setConfig({ ...config, token: result.token });
      } else {
        setError(result.error ?? 'Failed to rotate token');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setRotating(false);
    }
  }, [config]);

  // Handle permission level change
  const handlePermissionChange = useCallback((value: string) => {
    updateConfig({ permissionLevel: value as McpPermissionLevel });
  }, [updateConfig]);

  // Handle max rows change
  const handleMaxRowsBlur = useCallback(() => {
    const parsed = parseInt(maxRowsInput, 10);
    if (!isNaN(parsed) && parsed > 0 && parsed !== config?.maxRows) {
      updateConfig({ maxRows: parsed });
    }
  }, [maxRowsInput, config?.maxRows, updateConfig]);

  // Handle timeout change
  const handleTimeoutBlur = useCallback(() => {
    const parsed = parseInt(timeoutInput, 10);
    if (!isNaN(parsed) && parsed > 0 && parsed !== config?.queryTimeoutSeconds) {
      updateConfig({ queryTimeoutSeconds: parsed });
    }
  }, [timeoutInput, config?.queryTimeoutSeconds, updateConfig]);

  // Handle audit settings toggles
  const handleAuditEnabledChange = useCallback(async (checked: boolean) => {
    const ok = await updateConfig({ auditEnabled: checked });
    if (ok) {
      window.electronAPI?.analytics?.track('ui:mcp_audit_enabled_toggled', { enabled: checked }).catch(() => {});
    }
  }, [updateConfig]);

  const handleAuditIncludeSqlTextChange = useCallback(async (checked: boolean) => {
    const ok = await updateConfig({ auditIncludeSqlText: checked });
    if (ok) {
      window.electronAPI?.analytics?.track('ui:mcp_audit_include_sql_toggled', { enabled: checked }).catch(() => {});
    }
  }, [updateConfig]);

  // Handle privacy settings toggles
  const handleShareResultsChange = useCallback(async (checked: boolean) => {
    const ok = await updateConfig({ shareResults: checked });
    if (ok) {
      window.electronAPI?.analytics?.track('ui:mcp_share_results_toggled', { enabled: checked }).catch(() => {});
    }
  }, [updateConfig]);

  const handleShareEditorContentChange = useCallback(async (checked: boolean) => {
    const ok = await updateConfig({ shareEditorContent: checked });
    if (ok) {
      window.electronAPI?.analytics?.track('ui:mcp_share_editor_toggled', { enabled: checked }).catch(() => {});
    }
  }, [updateConfig]);

  const handleAllowConnectionsConnectChange = useCallback(async (checked: boolean) => {
    const ok = await updateConfig({ allowConnectionsConnect: checked });
    if (ok) {
      window.electronAPI?.analytics?.track('ui:mcp_allow_connections_connect_toggled', { enabled: checked }).catch(() => {});
    }
  }, [updateConfig]);

  const handleConnectionsAddPermissionChange = useCallback(async (value: string) => {
    const ok = await updateConfig({ connectionsAddPermission: value as ConnectionsAddPermission });
    if (ok) {
      window.electronAPI?.analytics?.track('ui:mcp_connection_add_permission_changed', { value }).catch(() => {});
    }
  }, [updateConfig]);

  const handleMetaInfoWritePermissionChange = useCallback(async (value: string) => {
    const ok = await updateConfig({ metaInfoWritePermission: value as MetaInfoWritePermission });
    if (ok) {
      window.electronAPI?.analytics?.track('ui:mcp_metainfo_write_permission_changed', { value }).catch(() => {});
    }
  }, [updateConfig]);

  const handleAllowScreenshotsChange = useCallback(async (checked: boolean) => {
    const ok = await updateConfig({ allowScreenshots: checked });
    if (ok) {
      window.electronAPI?.analytics?.track('ui:mcp_allow_screenshots_toggled', { enabled: checked }).catch(() => {});
    }
  }, [updateConfig]);

  // Handle port mode change
  const handlePortModeChange = useCallback((value: string) => {
    updateConfig({ portMode: value as McpPortMode });
  }, [updateConfig]);

  // Handle preferred port change
  const handlePreferredPortBlur = useCallback(() => {
    const parsed = parseInt(preferredPortInput, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 65535 && parsed !== config?.preferredPort) {
      updateConfig({ preferredPort: parsed });
    }
  }, [preferredPortInput, config?.preferredPort, updateConfig]);

  const handleOpenDocs = useCallback(() => {
    window.electronAPI?.openExternalUrl?.(
      'https://jamsql.com/docs/ai-integrations-mcp?utm_source=app&utm_medium=referral&utm_campaign=ai-integrations-settings'
    );
  }, []);

  // Copy config for Claude Code CLI (.mcp.json)
  const handleCopyForClaudeCode = useCallback(() => {
    if (!status?.running || !status.endpoint || !config?.token) return;
    const mcpConfig = {
      mcpServers: {
        'jam-sql': {
          type: 'http',
          url: status.endpoint,
          headers: {
            Authorization: `Bearer ${config.token}`,
          },
        },
      },
    };
    void writeClipboardText(JSON.stringify(mcpConfig, null, 2));
    setClaudeCodeCopied(true);
    setTimeout(() => setClaudeCodeCopied(false), 5000);
  }, [status?.running, status?.endpoint, config?.token]);

  // Show upsell view for browser mode
  if (isBrowserMode) {
    return <BrowserModeUpsell />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-sm text-destructive">{error ?? 'Failed to load configuration'}</div>
      </div>
    );
  }

  const endpoint = status?.running && status.port
    ? `http://127.0.0.1:${status.port}/mcp`
    : config.port
      ? `http://127.0.0.1:${config.port}/mcp`
      : 'Not running';

  const maskedToken = config.token
    ? `${config.token.slice(0, 8)}${'•'.repeat(20)}`
    : '';

  return (
    <div className="space-y-6 max-w-xl">
      <SkillSnippet />

      <Button
        variant="outline"
        size="lg"
        className="w-full justify-center gap-2"
        onClick={handleOpenDocs}
        data-testid="ai-integrations-how-to-use-btn"
      >
        <BookOpen className="h-5 w-5" />
        How to use AI Integrations
      </Button>

      {error && (
        <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          {error}
          <Button
            variant="ghost"
            size="sm"
            className="ml-2 h-6 px-2"
            onClick={() => setError(null)}
          >
            Dismiss
          </Button>
        </div>
      )}

      <div className="text-xs text-muted-foreground">
        The MCP server powers the jam-sql CLI and AI skill, giving your coding agents (Claude Code, Codex, Cursor, Copilot, and others) access to your databases. You can also configure it as a standalone MCP client for any MCP-compatible tool.
      </div>

      <div className="space-y-3">
        <div className="text-sm font-medium">MCP Server</div>

        <SettingsRow
          title="Enable MCP server"
          description="Start a local MCP server for AI tool integrations."
          htmlFor="mcp-server-enabled"
          testId="mcp-server-enabled-row"
        >
          <Switch
            id="mcp-server-enabled"
            checked={config.enabled}
            onCheckedChange={handleToggleEnabled}
            data-testid="mcp-server-enabled-toggle"
          />
        </SettingsRow>

        <SettingsRow
          title="Endpoint URL"
          description={status?.running ? 'Server is running.' : 'Server is not running.'}
          htmlFor="mcp-server-endpoint"
          testId="mcp-server-endpoint-row"
        >
          <div className="flex items-center gap-2">
            <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
              {endpoint}
            </code>
            <CopyButton value={status?.running ? endpoint : ''} label="endpoint" />
          </div>
        </SettingsRow>

        <SettingsRow
          title="Port mode"
          description={config.portMode === 'fixed'
            ? 'Use only the configured port. Fails if port is unavailable.'
            : 'Try preferred port first, then auto-select if unavailable.'}
          htmlFor="mcp-port-mode"
          testId="mcp-port-mode-row"
        >
          <Select
            value={config.portMode || 'auto'}
            onValueChange={handlePortModeChange}
          >
            <SelectTrigger className="w-[180px]" id="mcp-port-mode" data-testid="mcp-port-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto (recommended)</SelectItem>
              <SelectItem value="fixed">Fixed port</SelectItem>
            </SelectContent>
          </Select>
        </SettingsRow>

        <SettingsRow
          title={config.portMode === 'fixed' ? 'Fixed port' : 'Preferred port'}
          description={config.portMode === 'fixed'
            ? 'The exact port to use for the MCP server.'
            : 'The port to try first. If unavailable, another port will be selected.'}
          htmlFor="mcp-preferred-port"
          testId="mcp-preferred-port-row"
        >
          <Input
            id="mcp-preferred-port"
            className="w-[100px] text-right"
            inputMode="numeric"
            value={preferredPortInput}
            onChange={(e) => setPreferredPortInput(e.target.value)}
            onBlur={handlePreferredPortBlur}
            data-testid="mcp-preferred-port-input"
          />
        </SettingsRow>

        <SettingsRow
          title="Authentication token"
          description="Bearer token required for all MCP requests."
          htmlFor="mcp-server-token"
          testId="mcp-server-token-row"
        >
          <div className="flex flex-col gap-2 items-end">
            <div className="flex items-center gap-1">
              <code className="text-xs bg-muted px-2 py-1 rounded font-mono max-w-[180px] truncate">
                {showToken ? config.token : maskedToken}
              </code>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setShowToken(!showToken)}
                title={showToken ? 'Hide token' : 'Show token'}
              >
                {showToken ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <div className="flex items-center gap-1">
              <CopyButton value={config.token} label="token" />
              <Button
                variant="outline"
                size="sm"
                onClick={handleRotateToken}
                disabled={rotating}
                title="Generate a new token (invalidates the old one)"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${rotating ? 'animate-spin' : ''}`} />
                Rotate
              </Button>
            </div>
          </div>
        </SettingsRow>
      </div>

      <div className="space-y-3">
        <div className="text-sm font-medium">Quick Setup</div>

        <div className="rounded-md border border-border bg-background p-4" data-testid="copy-for-claude-code-row">
          <div className="flex items-start justify-between gap-4">
            <div className="grid gap-0.5 flex-1">
              <Label className="font-medium">Copy for Claude Code CLI</Label>
              <div className="text-xs text-muted-foreground">
                Copy the .mcp.json config to paste into your project root.
              </div>
              {claudeCodeCopied && (
                <div className="flex items-center gap-1.5 mt-1.5 text-xs text-green-600 dark:text-green-500">
                  <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
                  <span>Copied! Paste into .mcp.json in your project root.</span>
                </div>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyForClaudeCode}
              disabled={!status?.running}
              title={!status?.running ? 'MCP server must be running' : 'Copy .mcp.json config'}
              data-testid="copy-for-claude-code-btn"
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" />
              {claudeCodeCopied ? 'Copied!' : 'Copy Config'}
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="text-sm font-medium">Permission Level</div>

        <SettingsRow
          title="Default permission level"
          description={permissionDescriptions[config.permissionLevel]}
          htmlFor="mcp-permission-level"
          testId="mcp-permission-level-row"
          warning={config.permissionLevel === 'confirm' ? 'Write queries will require your approval before execution. Oracle PL/SQL debug tools become available at this level; starting a debug session shows you the block first, and an AI can read paused variables but cannot evaluate expressions that would run code. No level lets an AI run a notebook JavaScript cell or save a notebook to a path it chose — those are not database operations.' : undefined}
        >
          <Select
            value={config.permissionLevel}
            onValueChange={handlePermissionChange}
          >
            <SelectTrigger className="w-[240px]" id="mcp-permission-level" data-testid="mcp-permission-level-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="block">{permissionLabels.block}</SelectItem>
              <SelectItem value="read-only">{permissionLabels['read-only']}</SelectItem>
              <SelectItem value="confirm">{permissionLabels.confirm}</SelectItem>
            </SelectContent>
          </Select>
        </SettingsRow>
      </div>

      <div className="space-y-3">
        <div className="text-sm font-medium">Agent Capabilities</div>

        <SettingsRow
          title="Allow AI to connect using saved connections"
          description="Lets AI agents initiate a database connection using your saved credentials."
          htmlFor="mcp-allow-connections-connect"
          testId="mcp-allow-connections-connect-row"
          warning={!config.allowConnectionsConnect ? 'Disabled by default. Enable only if you want agents to initiate connections.' : undefined}
        >
          <Switch
            id="mcp-allow-connections-connect"
            checked={config.allowConnectionsConnect ?? false}
            onCheckedChange={handleAllowConnectionsConnectChange}
            data-testid="mcp-allow-connections-connect-toggle"
          />
        </SettingsRow>

        <SettingsRow
          title="Allow AI to add new connections"
          description="Lets AI agents create new database connections on your behalf. The connection is test-connected before saving; the password is encrypted at rest."
          htmlFor="mcp-connections-add-permission"
          testId="mcp-connections-add-permission-row"
          warning={config.connectionsAddPermission === 'allow' ? 'AI agents can write credentials to your local config without per-call confirmation.' : undefined}
        >
          <Select
            value={config.connectionsAddPermission ?? 'block'}
            onValueChange={handleConnectionsAddPermissionChange}
          >
            <SelectTrigger id="mcp-connections-add-permission" data-testid="mcp-connections-add-permission-select" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="block">Deny</SelectItem>
              <SelectItem value="confirm">Confirm each</SelectItem>
              <SelectItem value="allow">Allow</SelectItem>
            </SelectContent>
          </Select>
        </SettingsRow>

        <SettingsRow
          title="Allow AI to declare MetaInfo"
          description="Lets AI agents save loose foreign keys, enum columns, JSON columns, and reference-table flags for a database, or update one that already exists. Nothing in the database itself changes — entries are stored locally and badged AI-created (or AI-updated) until you edit, re-save or remove them."
          htmlFor="mcp-metainfo-write-permission"
          testId="mcp-metainfo-write-permission-row"
          warning={config.metaInfoWritePermission === 'allow' ? 'AI agents can save metadata without confirming each declaration.' : undefined}
        >
          <Select
            value={config.metaInfoWritePermission ?? 'block'}
            onValueChange={handleMetaInfoWritePermissionChange}
          >
            <SelectTrigger id="mcp-metainfo-write-permission" data-testid="mcp-metainfo-write-permission-select" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="block">Deny</SelectItem>
              <SelectItem value="confirm">Confirm each</SelectItem>
              <SelectItem value="allow">Allow</SelectItem>
            </SelectContent>
          </Select>
        </SettingsRow>

        <SettingsRow
          title="Allow AI screenshots"
          description="Allows AI agents to capture a screenshot of the app window when you ask for visual feedback."
          htmlFor="mcp-allow-screenshots"
          testId="mcp-allow-screenshots-row"
          warning="Screenshots may include sensitive data. Disable if unsure."
        >
          <Switch
            id="mcp-allow-screenshots"
            checked={config.allowScreenshots ?? true}
            onCheckedChange={handleAllowScreenshotsChange}
            data-testid="mcp-allow-screenshots-toggle"
          />
        </SettingsRow>
      </div>

      <div className="space-y-3">
        <div className="text-sm font-medium">Query Limits</div>

        <SettingsRow
          title="Max rows per query"
          description="Limit the number of rows returned by AI-executed queries."
          htmlFor="mcp-max-rows"
          testId="mcp-max-rows-row"
        >
          <Input
            id="mcp-max-rows"
            className="w-[120px] text-right"
            inputMode="numeric"
            value={maxRowsInput}
            onChange={(e) => setMaxRowsInput(e.target.value)}
            onBlur={handleMaxRowsBlur}
            data-testid="mcp-max-rows-input"
          />
        </SettingsRow>

        <SettingsRow
          title="Query timeout (seconds)"
          description="Maximum time to wait for query execution."
          htmlFor="mcp-timeout"
          testId="mcp-timeout-row"
        >
          <Input
            id="mcp-timeout"
            className="w-[120px] text-right"
            inputMode="numeric"
            value={timeoutInput}
            onChange={(e) => setTimeoutInput(e.target.value)}
            onBlur={handleTimeoutBlur}
            data-testid="mcp-timeout-input"
          />
        </SettingsRow>

      </div>

      <div className="space-y-3">
        <div className="text-sm font-medium">Audit & Logging</div>

        <SettingsRow
          title="Audit AI tool calls"
          description="Record tool calls with minimal metadata (tool name, connection, outcome). No SQL text included."
          htmlFor="mcp-audit-enabled"
          testId="mcp-audit-enabled-row"
        >
          <Switch
            id="mcp-audit-enabled"
            checked={config.auditEnabled ?? true}
            onCheckedChange={handleAuditEnabledChange}
            data-testid="mcp-audit-enabled-toggle"
          />
        </SettingsRow>

        <SettingsRow
          title="Include SQL text in audit"
          description="Include the full SQL query text in audit logs. May expose sensitive data."
          htmlFor="mcp-audit-include-sql"
          testId="mcp-audit-include-sql-row"
          warning={config.auditIncludeSqlText ? 'SQL text may contain sensitive information.' : undefined}
        >
          <Switch
            id="mcp-audit-include-sql"
            checked={config.auditIncludeSqlText ?? false}
            onCheckedChange={handleAuditIncludeSqlTextChange}
            disabled={!config.auditEnabled}
            data-testid="mcp-audit-include-sql-toggle"
          />
        </SettingsRow>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-muted-foreground" />
          <div className="text-sm font-medium">Privacy & Data Sharing</div>
        </div>

        <div className="text-xs text-muted-foreground">
          Privacy first: all data sharing is disabled by default. Enable only what you need.
        </div>

        <SettingsRow
          title="Share query results"
          description="Allow AI agents to access query results via the results_snapshot resource."
          htmlFor="mcp-share-results"
          testId="mcp-share-results-row"
        >
          <Switch
            id="mcp-share-results"
            checked={config.shareResults ?? false}
            onCheckedChange={handleShareResultsChange}
            data-testid="mcp-share-results-toggle"
          />
        </SettingsRow>

        <SettingsRow
          title="Share editor content"
          description="Allow AI agents to read SQL text from the editor via the editor_snapshot resource."
          htmlFor="mcp-share-editor"
          testId="mcp-share-editor-row"
        >
          <Switch
            id="mcp-share-editor"
            checked={config.shareEditorContent ?? false}
            onCheckedChange={handleShareEditorContentChange}
            data-testid="mcp-share-editor-toggle"
          />
        </SettingsRow>
      </div>
    </div>
  );
}
